import { computed, Directive, inject, input } from "@angular/core";
import { hlm, HlmBadge } from "@o2-fe/ui";
import { cva, VariantProps } from "class-variance-authority";
import { ClassValue } from "clsx";

/**
 * Status badge color variants
 * Add more status colors here as needed
 */
export const statusBadgeVariants = cva(
  "inline-flex items-center justify-center border px-3 py-1 text-xs font-medium leading-none [&&]:!rounded-full",
  {
    variants: {
      variant: {
        // User status
        ACTIVE: "[&&]:bg-green-50 [&&]:text-green-700 [&&]:border-green-600",
        INACTIVE: "[&&]:bg-red-50 [&&]:text-red-700 [&&]:border-red-600",

        // Quote status (lowercase)
        DRAFT: "[&&]:bg-gray-50 [&&]:text-gray-700 [&&]:border-gray-500",
        SENT: "[&&]:bg-yellow-50 [&&]:text-yellow-700 [&&]:border-yellow-500",
        ACCEPTED: "[&&]:bg-green-50 [&&]:text-green-700 [&&]:border-green-600",
        REJECTED: "[&&]:bg-red-50 [&&]:text-red-700 [&&]:border-red-600",

        // Contract status (UPPERCASE - new format)
        // DRAFT: quote status,
        SIGNED: "[&&]:bg-blue-50 [&&]:text-blue-700 [&&]:border-blue-600",
        SUSPENDED:
          "[&&]:bg-orange-50 [&&]:text-orange-700 [&&]:border-orange-600",
        // REJECTED: quote status,

        // Payment status (UPPERCASE)
        PLANNED: "[&&]:bg-gray-50 [&&]:text-gray-700 [&&]:border-gray-500",
        TO_APPROVE:
          "[&&]:bg-amber-50 [&&]:text-amber-700 [&&]:border-amber-500",
        TO_INVOICE: "[&&]:bg-blue-50 [&&]:text-blue-700 [&&]:border-blue-600",
        INVOICED: "[&&]:bg-cyan-50 [&&]:text-cyan-700 [&&]:border-cyan-600",
        PAID: "[&&]:bg-green-50 [&&]:text-green-700 [&&]:border-green-600",
        OVERDUE: "[&&]:bg-red-50 [&&]:text-red-700 [&&]:border-red-600",
        CANCELLED:
          "[&&]:bg-orange-50 [&&]:text-orange-700 [&&]:border-orange-600",

        // Subtask status (UPPERCASE)
        BACKLOG: "[&&]:bg-gray-100 [&&]:text-black [&&]:border-gray-400",
        TODO: "[&&]:bg-gray-50 [&&]:text-gray-700 [&&]:border-gray-200",
        IN_PROGRESS: "[&&]:bg-blue-50 [&&]:text-blue-900 [&&]:border-blue-300",
        PENDING_VALIDATION:
          "[&&]:bg-orange-50 [&&]:text-orange-800 [&&]:border-orange-200",
        PENDING_APPROVAL:
          "[&&]:bg-yellow-50 [&&]:text-yellow-700 [&&]:border-yellow-400",
        COMPLETED: "[&&]:bg-green-50 [&&]:text-green-900 [&&]:border-green-400",

        // Project status (UPPERCASE)
        PROSPECT:
          "[&&]:bg-purple-50 [&&]:text-purple-700 [&&]:border-purple-600",
        // SUSPENDED: shared with Contract status
        // IN_PROGRESS, COMPLETED: shared with Subtask status

        // Analytics status (UPPERCASE)
        DELETED: "[&&]:bg-gray-100 [&&]:text-gray-500 [&&]:border-gray-400",
      },
    },
    defaultVariants: {
      variant: "ACTIVE",
    },
  },
);

export type StatusBadgeVariant = VariantProps<typeof statusBadgeVariants>;

/**
 * Directive to apply status-based colors to badges
 *
 * Usage:
 * <span statusBadge [status]="'active'">Attivo</span>
 * <span statusBadge [status]="user.status">{{ user.status | statusLabel }}</span>
 */
@Directive({
  selector: "[statusBadge]",
  standalone: true,
  hostDirectives: [HlmBadge],
  host: {
    "[class]": "_computedClass()",
  },
})
export class StatusBadgeDirective {
  protected readonly _hlmBadge = inject(HlmBadge, { self: true });

  protected readonly _computedClass = computed(() =>
    hlm(
      statusBadgeVariants({
        variant: this.status() as StatusBadgeVariant["variant"],
      }),
      this.userClass(),
    ),
  );

  public readonly status = input.required<string>();
  public readonly userClass = input<ClassValue>("", { alias: "class" });
}
